import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
	CircularProgress,
	Box,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Checkbox,
	FormControlLabel,
	Divider,
	Button,
	Alert,
	useTheme,
	Skeleton,
	TextField,
	MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ProductCard from "../../components/ProductCard/ProductCard";
import { useSite } from "../../context/siteContext";
import PageTitle from "../../components/PageTitle/PageTitle";
import "./Products.css";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import PageNotFound from "../PageNotFound/PageNotFound";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import DescriptionIcon from "@mui/icons-material/Description";
import {
	ASSETS_BASE_URL,
	SERVER_BASE_URL,
	RSPL_LIST,
	DOCUMENT_EXTERNAL_ENABLED,
	DOCUMENT_EXTERNAL_URL,
	EXTERNAL_2D_ENABLED,
} from "../../config";
import { useOptions } from "../../context/optionContext";
import { useCart } from "../../context/cartContext";
import Swal from "sweetalert2";
import { useAuth } from "../../context/authContext";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import TopProductListItem from "../../components/TopProductListItem/TopProductListItem";
import {
	ALERT_MESSAGES,
	BUTTON,
	LABELS,
	PLACEHOLDER,
	UI_TEXT,
	ROUTES,
} from "../../constants";
import { useTranslation } from "react-i18next";
import MultipleModelViewer from "../../components/MultipleModelViewer/MultipleModelViewer";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import { getMachinesByIds } from "../../services/breadCrumbsService";
import { processModelFile, verifyResourceExists } from "../../services/modelService";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";
import { getTopPurchasedProduct } from "../../services/productsService";
import { getMachinesWithModels } from "../../services/myMachineService";
import { process2DModelFile } from "../../services/modelService";
import BomViewer from "../../components/BomViewer/BomViewer";
import BomProductQuickView from "../../components/BomProductQuickView/BomProductQuickView";
import { processModel } from "../../services/multipleModelViewer";
import {
	buildExternalLoginUrl,
	buildExternalTargetUrl,
	getMachineDocEntry,
	openExternalDocumentation,
} from "../../utilities/externalDocs";
import {
	indexBomTree,
	collectAncestorIds,
	MACHINE_NODE_PREFIX,
} from "../../utilities/bomUtils";
import {
	countAvailableModelsFromModelData,
	countDirectAvailableModelsFromModelData,
	hasDirect2DModelFromModelData,
	hasDirect3DModelFromModelData,
} from "../../utilities/selectBestModel";
import {
	buildRsplCodeToLabelMap,
	getRsplLabelForCode,
	parseRsplList,
} from "../../utilities/rspl";
import { trackEvent } from "../../utilities/analyticsService";
import { normalizeOrderQuantity } from "../../utilities/orderQuantity";

const PRODUCT_FILTER_SORT_KEYS = {
	NAME_ASC: "name_asc",
	NAME_DESC: "name_desc",
	NUMBER_ASC: "number_asc",
	NUMBER_DESC: "number_desc",
	PRICE_ASC: "price_asc",
	PRICE_DESC: "price_desc",
};

const parseJsonArray = (value, fallback = []) => {
	if (!value) return fallback;
	try {
		const parsed = typeof value === "string" ? JSON.parse(value) : value;
		return Array.isArray(parsed) ? parsed : fallback;
	} catch {
		return fallback;
	}
};

const extractFilename = (path) => {
	if (!path) return "";
	const parts = String(path).split("/");
	const lastPart = parts[parts.length - 1] || "";
	return lastPart.replace(/\.[^/.]+$/, "");
};

const build2dUrl = (pathValue) => {
	if (!pathValue) return "";
	let normalized = String(pathValue).trim();
	if (!normalized) return "";
	if (/^https?:\/\//i.test(normalized)) return normalized;
	normalized = normalized.replace(/\\/g, "/");
	normalized = normalized.replace(/^https?:\/\/[^/]+/i, "");
	normalized = normalized.replace(/^\/?stock-models\//i, "");
	normalized = normalized.replace(/^\/?models\//i, "");
	normalized = normalized.replace(/^\/?modelfiles\//i, "");
	normalized = normalized.replace(/^\/+/i, "");
	if (!/^2d\//i.test(normalized)) {
		normalized = `2D/${normalized}`;
	}
	if (!/\.pdf$/i.test(normalized)) {
		normalized = `${normalized}.pdf`;
	}
	return `${SERVER_BASE_URL}/stock-models/${normalized}`;
};

const normalize3DFile = (file) => {
	if (!file) return { glb: null, html: null, htmlV2: null };
	if (typeof file === "string") return { glb: null, html: file, htmlV2: null };
	return {
		glb: file.glb || null,
		html: file.html || null,
		htmlV2: file.htmlV2 || null,
	};
};

const isTruthyFlag = (value) =>
	value === true || value === "true" || value === "Y" || value === "1" || value === 1;

const Products = () => {
	// Create mapping from RSPL codes to product categories for efficient lookup
	// Create mapping from RSPL codes to product categories for efficient lookup



	const { machineID } = useParams(); // Extract machine ID from URL parameters
	const [products, setProducts] = useState([]);
	const [topProducts, setTopProducts] = useState([]);
	const topFetchTimer = useRef(null);
	const lastTrackedSearchRef = useRef("");
	// Bumped by "chip:navigate" so a same-site/same-machine re-ask (e.g. "show me the BOM for
	// this machine again") retries fetchAllData below even though machineID/selectedSite
	// never actually changed. Mirrors MyMachines.js's identical refreshTick pattern.
	const [refreshTick, setRefreshTick] = useState(0);
	useEffect(() => {
		const onChipNavigate = (e) => {
			if (!String(e?.detail?.path || "").startsWith("/machines")) return;
			setRefreshTick((n) => n + 1);
		};
		window.addEventListener("chip:navigate", onChipNavigate);
		return () => window.removeEventListener("chip:navigate", onChipNavigate);
	}, []);
	const [loading, setLoading] = useState(true);
	const [machine_name, setmachine_name] = useState("");
	const [machine_number, setmachine_number] = useState("");
	const [machineSerialForDocs, setMachineSerialForDocs] = useState("");
	const [machineHasDocumentation, setMachineHasDocumentation] = useState(false);
	const [error, setError] = useState(null);
	const [pageSize, setPageSize] = useState(50); // Number of products per page
	const [currentPage, setCurrentPage] = useState(1); // Current active page
	const [totalPages, setTotalPages] = useState(0); // Total pages for pagination
	const [productSearchTerm, setProductSearchTerm] = useState(""); // Search query for filtering products
	const [selectedFilters, setSelectedFilters] = useState(null); // null => All part types selected
	const { selectedSite, selectedSiteCurrency, currencySymbol, selectedSiteData } = useSite();
	const [sortOption, setSortOption] = useState("Price Desc");
	const [has3DModel, setHas3DModel] = useState(false);
	const [has2DModel, setHas2DModel] = useState(false);
	const [m3DButtonLoading, setM3DButtonLoading] = useState(false);
	const [hasMultipleModels, setHasMultipleModels] = useState(false);
	const [modelDataLoading, setModelDataLoading] = useState(true);
	const [modelData, setModelData] = useState({});
	const [bomTreeData, setBomTreeData] = useState([]);
	const [bomLoading, setBomLoading] = useState(true);
	const [checkedBomProductIds, setCheckedBomProductIds] = useState([]);
	const [selectedBomNodeId, setSelectedBomNodeId] = useState(null);
	const [expandedNodes, setExpandedNodes] = useState([]);
	const treeItemRefs = useRef({});
	const [modelsModalOpen, setModelsModalOpen] = useState(false);
	const [topProductsLoading, setTopProductsLoading] = useState(true);
	const [bomModalOpen, setBomModalOpen] = useState(false);
	const [bomProductQuickView, setBomProductQuickView] = useState({
		open: false,
		productId: null,
	});
	const [bomModalOpening, setBomModalOpening] = useState(false);
	const [bomSelectionReady, setBomSelectionReady] = useState(false);
	const syncSourceRef = useRef(null);
	const loadMoreRef = useRef(null);
	const loadMorePendingRef = useRef(false);
	const navigate = useNavigate();
	const location = useLocation();
	const { user, canAddToCart } = useAuth();
	const canShowAddToCart = canAddToCart();
	const canShowBulkAddButtons = canShowAddToCart;
	const theme = useTheme();
	const primaryColor = theme.palette.primary.main;
	const { addMultipleToCart } = useCart();
	const { t } = useTranslation();
	const { optionKeys } = useOptions();
	const locationIdForDocs =
		selectedSiteData?.customer_site_number ||
		selectedSiteData?.customer_site_name ||
		selectedSite ||
		"";
	const machineDocEntry = useMemo(
		() =>
			getMachineDocEntry({
				locationId: locationIdForDocs,
				serialNumber: machineSerialForDocs,
			}),
		[locationIdForDocs, machineSerialForDocs]
	);
	const shouldShowBomDocIcon = DOCUMENT_EXTERNAL_ENABLED
		? machineDocEntry?.HasDoc === "Y"
		: machineHasDocumentation;

	const normalizedMachineName = useMemo(
		() => (Array.isArray(machine_name) ? machine_name.join(", ") : machine_name || ""),
		[machine_name]
	);

	const normalizedMachineNumber = useMemo(
		() => (Array.isArray(machine_number) ? machine_number.join(", ") : machine_number || ""),
		[machine_number]
	);

	// Build the machine object to pass to Documentation
	const resolvedMachineForDocs = useMemo(() => {
		if (!machineID || machineID === 'ALL') {
			return {
				machine_id: 'ALL',
				machine_name: '',
				machine_number: '',
			};
		}

		return {
			machine_id: machineID,
			machine_name: normalizedMachineName || machine_name || '',
			machine_number: normalizedMachineNumber || machine_number || '',
		};
	}, [
		machineID,
		normalizedMachineName,
		normalizedMachineNumber,
		machine_name,
		machine_number,
	]);

	// const handleDocumentationNavigate = () => {
	// 	if (!DOCUMENT_EXTERNAL_ENABLED || !DOCUMENT_EXTERNAL_URL) {
	// 		const machineInfo = resolvedMachineForDocs;


	// 		return;
	// 	}

	// 	// External documentation logic...
	// 	const username = selectedSiteData?.amf_method_username_ln;
	// 	const password = selectedSiteData?.amf_method_password_ln;
	// 	const loginUrl = buildExternalLoginUrl({
	// 		templateUrl: DOCUMENT_EXTERNAL_URL,
	// 		username,
	// 		password,
	// 	});

	// 	if (!loginUrl) {
	// 		const machineInfo = resolvedMachineForDocs;
	// 		navigate(machineID ? `/documentation/${machineID}` : ROUTES.DOCUMENT, {
	// 			state: {
	// 				machine: machineInfo,
	// 				fromMachineCard: true,
	// 			}
	// 		});
	// 		return;
	// 	}

	// 	window.open(loginUrl, "_blank", "noopener,noreferrer");
	// };
	const handleDocumentationNavigate = () => {
		if (!DOCUMENT_EXTERNAL_ENABLED || !DOCUMENT_EXTERNAL_URL || !machineID) {
			const machineInfo = resolvedMachineForDocs;
			navigate(
				machineInfo.machine_id !== 'ALL'
					? `/documentation/${machineInfo.machine_id}`
					: ROUTES.DOCUMENT,
				{
					state: {
						machine: machineInfo,
						fromMachineCard: machineInfo.machine_id !== 'ALL',
					},
				}
			);
			return;
		}
		if (!machineDocEntry || machineDocEntry.HasDoc !== "Y") return;
		const username = selectedSiteData?.amf_method_username_ln;
		const password = selectedSiteData?.amf_method_password_ln;
		const loginUrl = buildExternalLoginUrl({
			templateUrl: DOCUMENT_EXTERNAL_URL,
			username,
			password,
		});
		const targetUrl = buildExternalTargetUrl({
			username,
			group: machineDocEntry.Group,
			machine: machineSerialForDocs,
		});
		if (!loginUrl || !targetUrl) {
			navigate(machineID ? `/documents/${machineID}` : ROUTES.DOCUMENT);
			return;
		}
		const targetWindow = window.open("about:blank", "_blank");
		openExternalDocumentation({ loginUrl, targetUrl, targetWindow });
	};

	const pageTitleText = useMemo(() => {
		if (normalizedMachineName || normalizedMachineNumber) {
			return [normalizedMachineName, normalizedMachineNumber].filter(Boolean).join(" | ");
		}
		return t("UI_TEXT.MACHINE");
	}, [normalizedMachineName, normalizedMachineNumber, t]);

	const machineNodeId = useMemo(
		() => `${MACHINE_NODE_PREFIX}-${machineID || "active"}`,
		[machineID]
	);

	const { flatNodes, nodeMap, machineProductIds } = useMemo(
		() => indexBomTree(bomTreeData),
		[bomTreeData]
	);

	const checkedProductIdSet = useMemo(
		() => new Set(checkedBomProductIds || []),
		[checkedBomProductIds]
	);

	const resolvedRsplList = useMemo(
		() => parseRsplList(optionKeys?.rsplList, RSPL_LIST),
		[optionKeys?.rsplList]
	);
	const rsplMapping = useMemo(
		() => buildRsplCodeToLabelMap(resolvedRsplList),
		[resolvedRsplList]
	);

	// Unique categories coming from RSPL + "Other"
	const categoryArray = useMemo(() => {
		const unique = Array.from(new Set((resolvedRsplList || []).map((item) => item.value)));
		unique.push(t('UI_TEXT.OTHER'));
		return unique;
	}, [resolvedRsplList, t]);

	const availableCategoryArray = useMemo(() => {
		if (!products.length) return [];
		const productTypeSet = new Set(
			products
				.map((product) => product.ProductType)
				.filter(Boolean)
		);
		const orderedKnownTypes = categoryArray.filter((type) => productTypeSet.has(type));
		const unknownTypes = Array.from(productTypeSet).filter(
			(type) => !categoryArray.includes(type)
		);
		return [...orderedKnownTypes, ...unknownTypes];
	}, [products, categoryArray]);

	const productTypeByProductId = useMemo(() => {
		const map = new Map();
		products.forEach((product) => {
			if (product?.product_id) {
				map.set(product.product_id, product.ProductType);
			}
		});
		return map;
	}, [products]);

	const productIdsByCategory = useMemo(() => {
		const map = new Map();
		products.forEach((product) => {
			const category = product?.ProductType;
			const productId = product?.product_id;
			if (!category || !productId) return;
			if (!map.has(category)) {
				map.set(category, []);
			}
			map.get(category).push(productId);
		});
		return map;
	}, [products]);

	const bomModelAvailability = useMemo(() => {
		const map = new Map();
		const registerEntry = (value, type, availability) => {
			if (value === undefined || value === null) return;
			const normalized = String(value).trim();
			if (!normalized) return;
			const key = `${type}:${normalized.toUpperCase()}`;
			if (!map.has(key)) {
				map.set(key, availability);
			}
		};
		const bomModels = modelData?.boms || [];
		bomModels.forEach((item) => {
			const threeD = normalize3DFile(item["3dfile"]);
			const has3D = Boolean(threeD.glb || threeD.html || threeD.htmlV2);
			const has2D = Boolean(item["2dfile"]);
			if (!has3D && !has2D) return;
			const productNumber =
				item?.product_number || item?.productNumber || item?.filename || "";
			const availability = {
				has3D,
				has2D,
				file3d: item["3dfile"] || "",
				file2d: item["2dfile"] || "",
				filename: productNumber,
				productNumbers: productNumber ? [productNumber] : [],
				raw: item,
			};
			registerEntry(productNumber, "pn", availability);
			registerEntry(item?.product_id, "id", availability);
			registerEntry(item?.productId, "id", availability);
		});
		return map;
	}, [modelData]);

	const hasBomData = machineProductIds.length > 0;

	const normalizedSearchTerm = productSearchTerm.trim().toLowerCase();

	const searchMatchIds = useMemo(() => {
		if (!normalizedSearchTerm) return [];
		return flatNodes
			.filter((node) => {
				const name = node.productName?.toLowerCase() || "";
				const number = node.productNumber?.toLowerCase() || "";
				return (
					name.includes(normalizedSearchTerm) ||
					number.includes(normalizedSearchTerm)
				);
			})
			.map((node) => node.nodeId);
	}, [flatNodes, normalizedSearchTerm]);

	const bulkAddCategories = useMemo(() => {
		const counts = products.reduce((acc, p) => {
			const cat = p.ProductType;
			if (!cat) return acc;
			acc[cat] = (acc[cat] || 0) + 1;
			if (Number(p.purchasable) === 1) acc[`${cat}__purch`] = (acc[`${cat}__purch`] || 0) + 1;
			return acc;
		}, {});

		return categoryArray
			.filter((cat) => cat !== t('UI_TEXT.OTHER'))
			.map((cat) => ({
				category: cat,
				totalCount: counts[cat] || 0,
				purchasableCount: counts[`${cat}__purch`] || 0,
			}))
			.filter(({ totalCount }) => totalCount > 0); // <-- category doesn't exist => no button
	}, [categoryArray, products]);


	const toI18nKey = (cat) =>
		`BUTTON.ADD_ALL_${String(cat).toUpperCase().replace(/[^A-Z0-9]+/g, "_")}_PARTS`;

	const getBulkButtonLabel = useCallback(
		(cat, purchasableCount) => {
			return `Add all ${cat} parts (${purchasableCount})`;
		},
		[t]
	);


	const sortOptions = useMemo(
		() => [
			t('GENERAL.NAME_ASC'),
			t('GENERAL.NAME_DESC'),
			t('GENERAL.NUMBER_ASC'),
			t('GENERAL.NUMBER_DESC'),
			t('GENERAL.PRICE_ASC'),
			t('GENERAL.PRICE_DESC'),
		],
		[t]
	);
	const getSortLabelFromKey = useCallback(
		(sortKey) => {
			switch (String(sortKey || "").trim().toLowerCase()) {
				case PRODUCT_FILTER_SORT_KEYS.NAME_ASC:
					return t('GENERAL.NAME_ASC');
				case PRODUCT_FILTER_SORT_KEYS.NAME_DESC:
					return t('GENERAL.NAME_DESC');
				case PRODUCT_FILTER_SORT_KEYS.NUMBER_ASC:
					return t('GENERAL.NUMBER_ASC');
				case PRODUCT_FILTER_SORT_KEYS.NUMBER_DESC:
					return t('GENERAL.NUMBER_DESC');
				case PRODUCT_FILTER_SORT_KEYS.PRICE_ASC:
					return t('GENERAL.PRICE_ASC');
				case PRODUCT_FILTER_SORT_KEYS.PRICE_DESC:
				default:
					return t('GENERAL.PRICE_DESC');
			}
		},
		[t]
	);
	const pageSizeOptions = [25, 50, 100];

	const handleProductSearchChange = (value) => {
		setCurrentPage(1);
		setProductSearchTerm(value);
	};

	const handlePageSizeChange = (event) => {
		setCurrentPage(1);
		setPageSize(Number(event.target.value));
	};

	const handleFilterChange = (event) => {
		const { value, checked } = event.target;
		syncSourceRef.current = "filters";
		setSelectedFilters((prevFilters) => {
			const currentFilters = prevFilters === null ? availableCategoryArray : prevFilters;
			const nextFilters = checked
				? Array.from(new Set([...currentFilters, value]))
				: currentFilters.filter((filter) => filter !== value);
			return nextFilters.length === availableCategoryArray.length ? null : nextFilters;
		});
	};

	const handleAllChange = (event) => {
		syncSourceRef.current = "filters";
		if (event.target.checked) {
			setSelectedFilters(null);
		} else {
			setSelectedFilters([]);
		}
	};

	const handleTreeSelection = (nodeId) => {
		if (nodeId === machineNodeId || !nodeMap.has(nodeId)) {
			setSelectedBomNodeId(null);
			return;
		}
		setSelectedBomNodeId(nodeId);
	};

	const handleBomProductClick = (node) => {
		if (!node?.productId) return;
		setBomProductQuickView({
			open: true,
			productId: node.productId,
		});
	};

	const showBomModelError = useCallback(() => {
		Swal.fire({
			icon: "error",
			title: t("ALERT_MESSAGES.ERROR"),
			text: t("ALERT_MESSAGES.MODEL_OPEN_FAILED"),
		});
	}, [t]);

	const openBomModelUrl = useCallback(async (url) => {
		if (!url) return false;
		const exists = await verifyResourceExists(url);
		if (!exists) {
			return false;
		}
		window.open(url);
		return true;
	}, []);

	const handleBomModelIconClick = useCallback(
		async ({ node, modelInfo, type }) => {
			if (!modelInfo || !node) return;
			try {
				if (type === "2d" && modelInfo.file2d) {
					if (EXTERNAL_2D_ENABLED) {
						const pdfUrl = build2dUrl(modelInfo.file2d);
						const opened = await openBomModelUrl(pdfUrl);
						if (!opened) {
							showBomModelError();
						}
					} else {
						const filename = extractFilename(modelInfo.file2d);
						if (!filename) {
							showBomModelError();
							return;
						}
						navigate(`/2d-model-viewer/${machineID}/${filename}`);
					}
					return;
				}
				const raw = modelInfo.raw || {};
				const threeD = raw["3dfile"];
				let glbPath = null;
				let htmlPath = null;
				let inhouse = false;
				if (threeD) {
					if (typeof threeD === "string") {
						htmlPath = threeD;
					} else {
						glbPath = threeD.glb || null;
						inhouse = Boolean(threeD.htmlV2);
						htmlPath = threeD.htmlV2 || threeD.html || null;
					}
				}

				if (type === "2d") {
					showBomModelError();
					return;
				}

				if (glbPath && !inhouse) {
					const filename =
						extractFilename(glbPath) ||
						modelInfo.filename ||
						node.productNumber ||
						node.product_number ||
						raw.product_number;
					if (!filename) {
						showBomModelError();
						return;
					}
					navigate(`/model-viewer/${machineID}/${filename}`);
					return;
				}

				if (!htmlPath) {
					showBomModelError();
					return;
				}

				const filename =
					extractFilename(htmlPath) ||
					modelInfo.filename ||
					node.productNumber ||
					node.product_number ||
					modelInfo?.raw?.product_number;
				if (!filename) {
					showBomModelError();
					return;
				}
				const productNumbers =
					(modelInfo.productNumbers && modelInfo.productNumbers.length > 0
						? modelInfo.productNumbers
						: [node.productNumber || node.product_number || filename].filter(Boolean)) || [];
				const response = await processModel(
					"/models/process-modelfile",
					filename,
					machine_name,
					(selectedSiteCurrency || "USD").toUpperCase(),
					user?.user_id,
					productNumbers,
					machineID,
					primaryColor,
					selectedSite,
					machine_number,
					inhouse ? "inhouse" : undefined,
					selectedSiteData?.site_id,
					selectedSiteData?.cus_id || selectedSiteData?.customer_id
				);
				if (response.status === 200 && response.data?.url) {
					const fullUrl = `${SERVER_BASE_URL}${response.data.url}`;
					if (inhouse || response.data.kind === "inhouse") {
						if (!(await verifyResourceExists(fullUrl))) {
							showBomModelError();
							return;
						}
						navigate("/html-model-viewer/" + machineID + "/" + extractFilename(response.data.url), {
							state: { modelUrl: fullUrl, fromPath: location.pathname + location.search + location.hash, machineName: machine_name, machineNumber: machine_number, kind: "inhouse" },
						});
					} else if (!(await openBomModelUrl(fullUrl))) {
						showBomModelError();
					}
				} else {
					showBomModelError();
				}
			} catch (error) {
				console.error("Failed to open BOM model", error);
				showBomModelError();
			}
		},
		[
			machineID,
			machine_name,
			machine_number,
			navigate,
			openBomModelUrl,
			selectedSite,
			selectedSiteCurrency,
			showBomModelError,
			primaryColor,
			user?.user_id,
		]
	);

	const handleCloseBomProductQuickView = () => {
		setBomProductQuickView({ open: false, productId: null });
	};

	const handleBomSelectionChange = (nextProductIds = []) => {
		syncSourceRef.current = "bom";
		setCheckedBomProductIds(nextProductIds);
	};

	const handleClearTreeSelection = () => {
		setSelectedBomNodeId(null);
	};

	const handleNavigateToProductDetails = (productId) => {
		if (!productId || !machineID) return;
		setBomModalOpen(false);
		navigate(ROUTES.PRODUCT_DETAILS(machineID, productId));
	};

	const handleOpenBomModal = () => {
		setBomModalOpening(true);
		setBomModalOpen(true);
	};

	// BOM Tree Viewer has always been a pure client-side modal (handleOpenBomModal above),
	// with no URL/query-param trigger at all — a deep link to this page could only ever
	// land on the flat parts list, never the tree itself. Lets CHIP (or any other deep
	// link) open it directly via ?open_bom=1. Waits for bomLoading to clear so the modal
	// isn't opened before bomTreeData is populated; the ref guard + query-param strip
	// keep it from re-firing on unrelated re-renders or a later page refresh/back-nav.
	const bomAutoOpenHandledRef = useRef(false);
	const bomDataForMachineIdRef = useRef(null);
	useEffect(() => {
		// User-reported: asking CHIP to open the BOM tree a second time (e.g. after leaving
		// this machine and coming back, or any later ?open_bom=1 request once the FIRST one
		// this mount already succeeded) silently did nothing — bomAutoOpenHandledRef was set
		// true once and never reset, so it permanently latched shut for the rest of this
		// page's mounted lifetime. Mirrors Support.js's ?open_create_case=1 fix: the ref only
		// blocks re-firing WHILE ?open_bom=1 is still present (guards extra renders landing
		// before the strip's navigate takes effect), and is reset back to false as soon as the
		// param is absent so a later, genuinely new request isn't swallowed.
		const params = new URLSearchParams(location.search);
		const hasParam = params.get("open_bom") === "1";
		if (!hasParam) {
			bomAutoOpenHandledRef.current = false;
			return;
		}
		if (bomAutoOpenHandledRef.current || bomLoading) return;
		if (bomDataForMachineIdRef.current !== machineID) return;
		bomAutoOpenHandledRef.current = true;
		handleOpenBomModal();
		params.delete("open_bom");
		const newSearch = params.toString();
		navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search, bomLoading, machineID]);

	// Same pattern as the ?open_bom=1 effect directly above, for the Model picker instead
	// of the BOM tree — lets CHIP (get_machine_model_viewer) trigger this page's own
	// "View Model" buttons (handle3DButtonClick/handle2DButtonClick below) via
	// ?open_model=1. Deliberately calls the SAME handlers a manual click would, rather
	// than forcing the popup open unconditionally — this matches the real site behaviour:
	// a single model (no split files/sub-assemblies/other type present) opens directly
	// (in-app viewer for glb, new tab for legacy html, in-app 2D viewer or new tab for pdf
	// depending on EXTERNAL_2D_ENABLED), and only a machine with multiple models (BOM/split
	// files, or both 2D+3D present) shows the picker — handle3DButtonClick/
	// handle2DButtonClick already contain that exact hasMultipleModels branch internally,
	// so this effect doesn't need to duplicate it.
	// This is a page-level trigger only — it does NOT touch MachineCard.js or
	// ModelButton.js (the shared, grid-reused components), unlike an earlier attempt this
	// session that made those components query-param-aware and caused a real bug
	// (recursive nested-modal rendering, and a regression in native click behavior).
	//
	// Gating on modelDataLoading alone was NOT enough (live-reproduced: popup opened showing
	// Machine Model (0)/BOM (0) for a machine confirmed to have both) — machineID and
	// location.search change in the SAME render (one navigate() call), so this effect and the
	// modelData-fetch effect below both close over the SAME stale pre-navigation
	// modelDataLoading value in that first render; a setState in one effect can't retroactively
	// fix what another effect in the same commit already read. Also depend on machineID and
	// additionally require modelDataForMachineIdRef.current === machineID (set by the fetch
	// effect only once it has actually resolved data for THIS machineID) so this effect keeps
	// bailing out — across however many renders it takes — until data is genuinely fresh.
	const modelAutoOpenHandledRef = useRef(false);
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const hasParam = params.get("open_model") === "1";
		// User-reported: same never-resets latch as bomAutoOpenHandledRef above — once this
		// effect successfully auto-opened a model for the FIRST ?open_model=1 this page saw,
		// modelAutoOpenHandledRef stayed true forever, so ANY later ?open_model=1 (even for a
		// different machine navigated to without a full remount) silently no-opped. Reset the
		// ref back to false whenever the param is absent (Support.js's ?open_create_case=1
		// pattern) so it only ever guards a genuine same-param re-fire, never a later request.
		if (!hasParam) {
			modelAutoOpenHandledRef.current = false;
			return;
		}
		if (modelAutoOpenHandledRef.current || modelDataLoading) return;
		if (modelDataForMachineIdRef.current !== machineID) return;
		modelAutoOpenHandledRef.current = true;
		// Strip the query param BEFORE triggering the button handler below, not after —
		// handle3DButtonClick's single-glb-model branch calls navigate() itself (to
		// /model-viewer/...), and since both calls run synchronously in the same tick,
		// whichever navigate() call is LAST wins. Stripping first (replace, so it doesn't
		// add a history entry of its own) means a subsequent real navigate from the
		// handler is the one left standing; doing it after was clobbering that
		// navigation and leaving the user stuck back on this same machine page.
		params.delete("open_model");
		const newSearch = params.toString();
		navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
		// Prefer 3D (matches selectBestModelFromModelData's priority order on the backend);
		// fall back to 2D only when there's no 3D model at all.
		if (has3DModel) {
			handle3DButtonClick();
		} else if (has2DModel) {
			handle2DButtonClick();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search, modelDataLoading, machineID, has3DModel, has2DModel]);

	const getPriceForCurrency = (product, currency) => {
		if (!product || !currency) return 0;

		const priceKey = `price_${currency}`;
		const currencyPrice = product[priceKey];
		const finalPrice = product.purchasable ? currencyPrice : 0;

		// Convert to number if it's a string
		if (!finalPrice && finalPrice !== 0) return 0;
		const cleanPrice = String(finalPrice).replace(/[^\d.-]/g, '');
		const numericPrice = parseFloat(cleanPrice);
		return isNaN(numericPrice) ? 0 : numericPrice;
	};

	const sortProductsByOption = (items, value) => {
		if (!value) return items;
		const sortedProducts = [...items];

		switch (value) {
			case t('GENERAL.NAME_ASC'):
				sortedProducts.sort((a, b) =>
					(a.product_name || "").trim().localeCompare((b.product_name || "").trim())
				);
				break;
			case t('GENERAL.NAME_DESC'):
				sortedProducts.sort((a, b) =>
					(b.product_name || "").trim().localeCompare((a.product_name || "").trim())
				);
				break;
			case t('GENERAL.NUMBER_ASC'):
				sortedProducts.sort((a, b) =>
					(a.product_number || "").trim().localeCompare((b.product_number || "").trim())
				);
				break;
			case t('GENERAL.NUMBER_DESC'):
				sortedProducts.sort((a, b) =>
					(b.product_number || "").trim().localeCompare((a.product_number || "").trim())
				);
				break;
			case t('GENERAL.PRICE_ASC'):
				sortedProducts.sort((a, b) => {
					const priceA = getPriceForCurrency(a, selectedSiteCurrency);
					const priceB = getPriceForCurrency(b, selectedSiteCurrency);
					return priceA - priceB;
				});
				break;
			case t('GENERAL.PRICE_DESC'):
				sortedProducts.sort((a, b) => {
					const priceA = getPriceForCurrency(a, selectedSiteCurrency);
					const priceB = getPriceForCurrency(b, selectedSiteCurrency);
					return priceB - priceA;
				});
				break;
			default:
				return items;
		}

		return sortedProducts;
	};

	const handleSortChange = (value) => {
		setSortOption(value);

		if (!value) return;

		setProducts((prevProducts) => sortProductsByOption(prevProducts, value));
	};

	// Bulk add by RSPL category (e.g., Critical, Maintenance)
	const addAllByCategory = async (categoryLabel) => {
		try {
			const candidates = products.filter(p => p.ProductType === categoryLabel);
			if (candidates.length === 0) {
				Swal.fire({ icon: 'info', title: t('CART.NO_PARTS_FOUND'), text: `${t('CART.NO_PARTS_UNDER_CATEGORY').replace('{categoryLabel}', categoryLabel)}` });
				return;
			}
			const purchasable = candidates.filter(p => Number(p.purchasable) === 1);
			const skipped = candidates.length - purchasable.length;
			if (purchasable.length === 0) {
				Swal.fire({ icon: 'info', title: t('CART.NO_PURCHASABLE_PARTS'), text: `${t('CART.ALL_CATEGORY_PARTS_ARE_NON_PURCHASABLE').replace('{categoryLabel}', categoryLabel)}` });
				return;
			}
			const items = purchasable.map(p => ({
				...p,
				ProductID: p.product_id,
				MachineID: machineID || p.machine_id,
				Quantity: normalizeOrderQuantity(1, p),
				Site: selectedSite,
			}));
			await addMultipleToCart(items);
			Swal.fire({ icon: 'success', title: t('CART.ADDED_TO_CART_1'), text: `${t('CART.ADDED_X_CATEGORY_PARTS_SKIPPED_Y').replace('{count}', purchasable.length).replace('{categoryLabel}', categoryLabel).replace('{skipped}', skipped)}` });
		} catch (e) {
			Swal.fire({ icon: 'error', title: t('GENERAL.FAILED'), text: t('CART.COULD_NOT_ADD_PARTS_TO_CART') });
		}
	};

	useEffect(() => {
		const fetchMachineDetails = async () => {
			if (!machineID) return;
			try {
				const reqBody = {
					machines: [machineID],
				};

				const response = await getMachinesByIds(reqBody);

				if (response.status && Array.isArray(response.data) && response.data.length > 0) {
					const machineData = response.data[0] || {};
					const resolvedName =
						machineData.sm_machine_name ||
						machineData.machine_name ||
						machineData.description ||
						"";
					setmachine_name(resolvedName || "");
					setmachine_number(machineData.machine_number || machineID || "");
					setMachineSerialForDocs(
						machineData.machine_product_name ||
						machineData.machine_serial_number ||
						machineData.machine_number ||
						machineID ||
						""
					);
					setMachineHasDocumentation(isTruthyFlag(machineData.has_document));
				} else {
					setError("Failed to fetch data: No success flag.");
				}
			} catch (err) {
				setError("Failed to fetch data: " + err.message);
			}
		};

		fetchMachineDetails();
	}, [machineID]);

	// The model-metadata fetch effect below only re-fires once `machine_number` (derived
	// from fetchMachineDetails above) settles for the NEW machine — but `machine_number` is
	// itself async, so there's a render window right after a machineID change where
	// modelDataLoading is still stale `false` from the PREVIOUS machine. The ?open_model=1
	// auto-open effect only guards on modelDataLoading, so during that window it fires
	// immediately with stale/empty modelData — this is why CHIP's deep link sometimes opened
	// the model popup empty. bomLoading doesn't have this race because its fetch effect
	// Was: a useEffect resetting modelDataLoading to true on [machineID] alone — INSUFFICIENT,
	// because within the SAME render where machineID/location.search both change (one
	// navigate() call changes both atomically), every effect in that commit — including this
	// one and the ?open_model=1 auto-open effect below — still closes over the SAME stale
	// pre-navigation value of modelDataLoading; a setState call in one effect cannot
	// retroactively change what another effect in the SAME commit already read. Live-reproduced
	// via screenshots: CHIP's popup still showed Machine Model (0)/BOM (0) for a machine that
	// genuinely has both, while manually clicking the button on the same machine showed (1)/(1).
	// Fixed properly below by tracking, via a ref (synchronous, no extra render needed), which
	// machineID the currently-loaded modelData actually belongs to, and gating the auto-open
	// effect on that ref matching the CURRENT machineID — not just on modelDataLoading.
	const modelDataForMachineIdRef = useRef(null);

	// Fetch model metadata (assets cache) similar to MachineCard and derive availability
	useEffect(() => {
		const fetchModelData = async () => {
			if (!selectedSite || !machine_number) return;
			setModelDataLoading(true);
			try {
				const resp = await getMachinesWithModels({
					site_number: selectedSite,
					page: 1,
					limit: 50,
					searchTerm: machine_number,
				});
				const row = (resp?.data || []).find(r => r.machine_number === machine_number);
				if (row && row.has_document !== undefined) {
					setMachineHasDocumentation(isTruthyFlag(row.has_document));
				}
				const md = row?.model_data || {};
				setModelData(md);
				const has2D = hasDirect2DModelFromModelData(md);
				const has3D = hasDirect3DModelFromModelData(md);
				const multiple =
					countDirectAvailableModelsFromModelData(md) > 0 &&
					countAvailableModelsFromModelData(md) > 1;
				setHas2DModel(has2D);
				setHas3DModel(has3D);
				setHasMultipleModels(multiple);
			} catch (e) {
				// Fallback to no models
				setModelData({});
				setHas2DModel(false);
				setHas3DModel(false);
				setHasMultipleModels(false);
			} finally {
				setModelDataLoading(false);
				// Marks modelData as resolved (successfully or not) for THIS machineID —
				// set regardless of outcome so a machine that genuinely has zero models
				// doesn't hang the auto-open effect forever waiting for a ref update.
				modelDataForMachineIdRef.current = machineID;
			}
		};
		fetchModelData();
	}, [selectedSite, machine_number]);

	const handle3DButtonClick = async () => {
		setM3DButtonLoading(true); // Show loading state

		try {
			// If multiple models available, open the viewer instead of direct
			if (hasMultipleModels) {
				setModelsModalOpen(true);
				setM3DButtonLoading(false);
				return;
			}
			// modelData["3dfile"] is the modern { glb, html, json } shape (or a legacy
			// plain HTML path string) - normalize before treating it as a URL. glb opens
			// in-app (same viewer route MultipleModelViewer/SingleProduct.js use); html
			// opens in a new tab, matching those same call sites.
			const main3d = normalize3DFile(modelData?.["3dfile"]);
			if (main3d.glb) {
				// pathname only, never location.search - a one-time trigger param like
				// ?open_model=1 must never be carried into the "back to machines" target
				// (the CHIP auto-open effect above already stripped it from the URL bar
				// via its own navigate() right before calling this, but location.search
				// in THIS closure is still the stale pre-strip value until the next
				// render - using it here would silently re-attach ?open_model=1 to the
				// model-viewer page's own "back" button, re-triggering the whole auto-open
				// flow every time the user tries to go back).
				navigate(`/model-viewer/${machineID}/${extractFilename(main3d.glb)}`, {
					state: { fromPath: location.pathname },
				});
				setM3DButtonLoading(false);
				return;
			}
			if (main3d.html) {
				window.open(`${SERVER_BASE_URL}${main3d.html}`);
				setM3DButtonLoading(false);
				return;
			}
			const response = await processModelFile({
				filename: machine_number,
				machinename: machine_name,
				currency: selectedSiteCurrency.toUpperCase(),
				loggedinUser: user.user_id,
				ecommachineid: machineID,
				baseColor: theme.palette.primary.main,
				selectedSite,
				site_id: selectedSiteData?.site_id || "",
				cus_id: selectedSiteData?.cus_id || selectedSiteData?.customer_id || "",
				machinenumber: machine_number,
			});

			if (response.status === 200) {
				const backendUrl = SERVER_BASE_URL || "";
				const fullUrl = backendUrl + response.data.url;
				setM3DButtonLoading(false);
				window.open(fullUrl); // Open 3D model in new tab
			}
		} catch (err) {
			console.error("Error fetching the model:", err);
		}
	};

	const handle2DButtonClick = async () => {
		setM3DButtonLoading(true);
		try {
			if (hasMultipleModels) {
				setModelsModalOpen(true);
				setM3DButtonLoading(false);
				return;
			}
			// If cached 2D file exists, open directly
			if (modelData?.["2dfile"]) {
				window.open(`${SERVER_BASE_URL}${modelData["2dfile"]}`);
				setM3DButtonLoading(false);
				return;
			}
			const response = await process2DModelFile({
				filename: machine_number,
				machinenumber: machine_number,
			});
			if (response.status === 200) {
				const backendUrl = SERVER_BASE_URL || "";
				const fullUrl = backendUrl + response.data.url;
				setM3DButtonLoading(false);
				window.open(fullUrl);
			}
		} catch (err) {
			console.error("Error fetching the 2D model:", err);
		} finally {
			setM3DButtonLoading(false);
		}
	};

	useEffect(() => {
		const fetchAllData = async () => {
			// Don't fetch if essential data is missing
			if (!machineID || !selectedSite || selectedSite === "") {
				return;
			}

			setLoading(true); // Set loading at start of fetch
			setBomLoading(true);
			setBomSelectionReady(false);
			setError(null); // Clear previous errors

			try {
				// Fetch products
				const productsReqBody = {
					machine_id: machineID,
					site_number: selectedSite,
				};

				const productsResponse = await getProductsByMachineId(productsReqBody);

				if (productsResponse.status) {
					const { products: machineProducts, bomTree } = normalizeMachineProductsResponse(productsResponse);
					const productsWithType = machineProducts.map((product) => {
						const productType = getRsplLabelForCode(
							product.product_rspl_code,
							rsplMapping,
							t('UI_TEXT.OTHER')
						);
						return { ...product, ProductType: productType };
					});

					const defaultSortLabel = getSortLabelFromKey(optionKeys?.productsDefaultSortKey);
					setSortOption(defaultSortLabel);
					const sortedProducts = sortProductsByOption(productsWithType, defaultSortLabel);
					setBomTreeData(bomTree || []);
					// User-reported 2026-07-22: the BOM tree's selected node vanished whenever CHIP
					// re-navigated here for an UNRELATED reason (e.g. re-asking about a different
					// part of this same machine) — this refetch is ALSO retriggered by the same
					// chip:navigate event (via refreshTick above) that drives a fresh fetchAllData,
					// and this unconditional setSelectedBomNodeId(null) ran on every fresh fetch
					// regardless of whether the previously-selected node still exists in the new
					// BOM tree. Same class of bug/fix as OrderHistory.js's setSelectedOrder(prev)
					// preservation: only clear the selection when it's genuinely gone from the
					// fresh data, otherwise keep it (remapped against the fresh nodeMap so a
					// selection surviving a refetch is never stale).
					setSelectedBomNodeId((prevSelectedId) => {
						if (!prevSelectedId) return null;
						const { nodeMap: freshBomNodeMap } = indexBomTree(bomTree || []);
						return freshBomNodeMap.has(prevSelectedId) ? prevSelectedId : null;
					});
					setProducts(sortedProducts);
					setTotalPages(Math.ceil(productsWithType.length / pageSize));
					setDefaultFilters(productsWithType);
					// Show list immediately; don't block on secondary calls
					setLoading(false);
				} else {
					setBomTreeData([]);
					setError("Failed to fetch products: No success flag.");
				}

				// Debounced background fetch for top products
				if (topFetchTimer.current) clearTimeout(topFetchTimer.current);
				topFetchTimer.current = setTimeout(async () => {
					try {
						setTopProductsLoading(true);
						const topProductsReqBody = {
							machine_id: machineID,
							site_number: selectedSite,
						};
						const topProductsResponse = await getTopPurchasedProduct(topProductsReqBody);
						if (topProductsResponse.status) {
							setTopProductsLoading(false);
							setTopProducts(topProductsResponse.data);
						} else {
							setTopProductsLoading(false);
							setTopProducts([]);
						}
					} catch (topProductsError) {
						// Do not surface errors to the main UI
						setTopProductsLoading(false);
						setTopProducts([]);
						console.warn("Failed to fetch top products:", topProductsError);
					}
				}, 400);
			} catch (err) {
				setError("Failed to fetch products: " + err.message);
				setBomTreeData([]);
			} finally {
				setBomLoading(false);
				setLoading(false); // Always set loading to false when done
				// Marks BOM data as resolved for THIS machineID — same defensive fix as the
				// model-viewer race below, applied here pre-emptively since the underlying
				// React effect-timing issue (a sibling effect reading a stale pre-navigation
				// loading flag in the same commit as a machineID/location.search change) is
				// identical in principle, even though not observed broken for BOM yet.
				bomDataForMachineIdRef.current = machineID;
			}
		};

		fetchAllData();
		return () => {
			if (topFetchTimer.current) clearTimeout(topFetchTimer.current);
		};
	}, [
		getSortLabelFromKey,
		machineID,
		optionKeys?.productsDefaultFilterTypes,
		optionKeys?.productsDefaultSortKey,
		pageSize,
		selectedSite,
		refreshTick,
	]);

	const setDefaultFilters = (productsWithType = []) => {
		const allDetectedTypes = Array.from(
			new Set(
				(productsWithType || [])
					.map((product) => product.ProductType)
					.filter(Boolean)
			)
		);
		const configuredTypes = parseJsonArray(optionKeys?.productsDefaultFilterTypes, ["__ALL__"])
			.map((value) => String(value || "").trim())
			.filter(Boolean);
		if (configuredTypes.length === 0 || configuredTypes.includes("__ALL__")) {
			setSelectedFilters(null);
			return;
		}
		const selectedConfiguredTypes = allDetectedTypes.filter((type) =>
			configuredTypes.includes(type)
		);
		setSelectedFilters(
			selectedConfiguredTypes.length === allDetectedTypes.length ? null : selectedConfiguredTypes
		);
	};

	const allPartTypesSelected = selectedFilters === null;
	const effectiveSelectedFilters = allPartTypesSelected ? availableCategoryArray : selectedFilters;

	const categoryFilteredProducts = useMemo(() => {
		if (allPartTypesSelected) return products;
		if (availableCategoryArray.length > 0 && effectiveSelectedFilters.length === 0) return [];
		return products.filter((item) => effectiveSelectedFilters.includes(item.ProductType));
	}, [allPartTypesSelected, products, effectiveSelectedFilters, availableCategoryArray]);

	const allCategoriesSelected =
		availableCategoryArray.length > 0 &&
		allPartTypesSelected;

	const textFilteredProducts = useMemo(() => {
		if (!normalizedSearchTerm) return categoryFilteredProducts;
		return categoryFilteredProducts.filter(
			(item) =>
				item.product_name?.toLowerCase().includes(normalizedSearchTerm) ||
				item.product_number?.toLowerCase().includes(normalizedSearchTerm)
		);
	}, [categoryFilteredProducts, normalizedSearchTerm]);

	const filteredProducts = useMemo(() => {
		if (!hasBomData) return textFilteredProducts;
		if (checkedProductIdSet.size === 0) return [];
		return textFilteredProducts.filter((item) =>
			checkedProductIdSet.has(item.product_id)
		);
	}, [textFilteredProducts, checkedProductIdSet, hasBomData]);
	useEffect(() => {
		if (!normalizedSearchTerm) return;

		const resultCount = filteredProducts.length;
		const trackingKey = `${normalizedSearchTerm}|${selectedSite}|${machineID}|products_list|${resultCount}`;

		if (lastTrackedSearchRef.current === trackingKey) return;

		lastTrackedSearchRef.current = trackingKey;

		trackEvent("search_performed", {
			search_term: productSearchTerm.trim(),
			search_source: "products_list",
			page_name: "Products List",
			page_path: `${location.pathname}${location.search || ""}`,
			result_count: resultCount,
			has_results: resultCount > 0,
			no_results: resultCount === 0,
			machine_id: machineID,
			machine_number,
		});
	}, [
		filteredProducts.length,
		machineID,
		machine_number,
		normalizedSearchTerm,
		productSearchTerm,
		selectedSite,
		location.pathname,
		location.search,
	]);
	const filtersActive =
		!allPartTypesSelected &&
		effectiveSelectedFilters.length > 0 &&
		effectiveSelectedFilters.length < availableCategoryArray.length;

	const highlightedProductIds = useMemo(() => {
		if (!filtersActive) return new Set();
		return new Set(filteredProducts.map((item) => item.product_id));
	}, [filtersActive, filteredProducts]);

	const shouldShowBomSelectionAlert =
		hasBomData && bomSelectionReady && !bomLoading && checkedBomProductIds.length === 0;

	const commonBomViewerProps = {
		title: t("UI_TEXT.BOM_TREE_TITLE"),
		helperText: t("UI_TEXT.BOM_TREE_HELPER"),
		clearSelectionLabel: t("UI_TEXT.CLEAR_BOM_SELECTION"),
		noDataLabel: t("UI_TEXT.NO_BOM_DATA"),
		bomSearchPlaceholder: t("UI_TEXT.BOM_TREE_SEARCH_PLACEHOLDER"),
		machineName: normalizedMachineName || t("UI_TEXT.MACHINE"),
		machineNumber: normalizedMachineNumber || machineID || "",
		machineNodeId,
		machineProductIds,
		treeData: bomTreeData,
		selectedNodeId: selectedBomNodeId,
		expandedNodes,
		searchTerm: productSearchTerm,
		checkedProductIds: checkedBomProductIds,
		onNodeSelect: handleTreeSelection,
		onSelectionChange: handleBomSelectionChange,
		onClearSelection: handleClearTreeSelection,
		onToggle: setExpandedNodes,
		filtersActive,
		highlightedProductIds,
		expandAllLabel: t("UI_TEXT.EXPAND_ALL"),
		collapseAllLabel: t("UI_TEXT.COLLAPSE_ALL"),
		modalButtonLabel: t("UI_TEXT.VIEW_BOM_IN_MODAL"),
		isLoading: bomLoading,
		onProductClick: handleBomProductClick,
		modelAvailability: bomModelAvailability,
		onModelIconClick: handleBomModelIconClick,
		machineHasDocumentation: shouldShowBomDocIcon,
		onMachineDocumentationClick: handleDocumentationNavigate,
	};


	useEffect(() => {
		if (!hasBomData || syncSourceRef.current !== "filters") return;
		if (allPartTypesSelected) {
			setCheckedBomProductIds((prev) => {
				if (prev.length === machineProductIds.length) {
					const prevSet = new Set(prev);
					const isSame = machineProductIds.every((id) => prevSet.has(id));
					if (isSame) return prev;
				}
				return machineProductIds;
			});
			syncSourceRef.current = null;
			return;
		}
		const selectedCategorySet = new Set(effectiveSelectedFilters);
		const nextCheckedSet = new Set();
		selectedCategorySet.forEach((category) => {
			const productIds = productIdsByCategory.get(category) || [];
			productIds.forEach((productId) => nextCheckedSet.add(productId));
		});
		const nextChecked = machineProductIds.filter((productId) =>
			nextCheckedSet.has(productId)
		);
		setCheckedBomProductIds((prev) => {
			if (prev.length === nextChecked.length) {
				const prevSet = new Set(prev);
				const isSame = nextChecked.every((id) => prevSet.has(id));
				if (isSame) return prev;
			}
			return nextChecked;
		});
		syncSourceRef.current = null;
	}, [allPartTypesSelected, effectiveSelectedFilters, hasBomData, productIdsByCategory, machineProductIds]);

	useEffect(() => {
		if (!hasBomData || syncSourceRef.current !== "bom") return;
		const selectedTypeSet = new Set();
		checkedBomProductIds.forEach((productId) => {
			const type = productTypeByProductId.get(productId);
			if (type) {
				selectedTypeSet.add(type);
			}
		});
		const nextSelectedFilters = availableCategoryArray.filter((category) =>
			selectedTypeSet.has(category)
		);
		setSelectedFilters((prev) => {
			const normalizedPrev = prev === null ? availableCategoryArray : prev;
			if (normalizedPrev.length === nextSelectedFilters.length) {
				const prevSet = new Set(normalizedPrev);
				const isSame = nextSelectedFilters.every((value) => prevSet.has(value));
				if (isSame) return prev;
			}
			return nextSelectedFilters.length === availableCategoryArray.length ? null : nextSelectedFilters;
		});
		syncSourceRef.current = null;
	}, [
		checkedBomProductIds,
		hasBomData,
		productTypeByProductId,
		availableCategoryArray,
	]);

	useEffect(() => {
		const pages = Math.ceil(filteredProducts.length / pageSize);
		setTotalPages(pages);
		setCurrentPage((prevPage) => {
			const maxPage = Math.max(pages, 1);
			if (prevPage > maxPage) {
				return maxPage;
			}
			return prevPage < 1 ? 1 : prevPage;
		});
	}, [filteredProducts, pageSize]);

	useEffect(() => {
		setCurrentPage(1);
	}, [normalizedSearchTerm, selectedFilters, hasBomData, sortOption]);

	useEffect(() => {
		treeItemRefs.current = {};
	}, [bomTreeData]);

	useEffect(() => {
		if (bomLoading) {
			setBomSelectionReady(false);
		}
		if (flatNodes.length === 0) {
			setCheckedBomProductIds([]);
			if (!bomLoading) {
				setBomSelectionReady(true);
			}
			return;
		}
		const seen = new Set();
		const allIds = [];
		flatNodes.forEach((node) => {
			if (node.productId && !seen.has(node.productId)) {
				seen.add(node.productId);
				allIds.push(node.productId);
			}
		});
		setCheckedBomProductIds((prev) => {
			if (prev.length === allIds.length) {
				const prevSet = new Set(prev);
				const isSame = allIds.every((id) => prevSet.has(id));
				if (isSame) {
					return prev;
				}
			}
			return allIds;
		});
		setBomSelectionReady(true);
	}, [flatNodes]);

	useEffect(() => {
		if (!hasBomData) {
			setSelectedBomNodeId(null);
		}
	}, [hasBomData]);

	useEffect(() => {
		setExpandedNodes([machineNodeId]);
		setSelectedBomNodeId(null);
	}, [machineNodeId]);

	useEffect(() => {
		if (bomModalOpen) {
			const timer = setTimeout(() => setBomModalOpening(false), 300);
			return () => clearTimeout(timer);
		}
		setBomModalOpening(false);
	}, [bomModalOpen]);

	useEffect(() => {
		if (!normalizedSearchTerm || searchMatchIds.length === 0) {
			return;
		}
		const targetId = searchMatchIds[0];
		const ancestors = collectAncestorIds(targetId, nodeMap);
		if (ancestors.length > 0 || targetId) {
			setExpandedNodes((prev) => {
				const unique = new Set(prev);
				ancestors.forEach((id) => unique.add(id));
				unique.add(machineNodeId);
				return Array.from(unique);
			});
		}
		if (!bomModalOpen) {
			return;
		}
		const scrollToNode = treeItemRefs.current?.__scrollToNode;
		if (typeof scrollToNode === "function") {
			scrollToNode(targetId);
			return;
		}
		const targetNode = treeItemRefs.current[targetId];
		if (targetNode && targetNode.scrollIntoView) {
			targetNode.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [normalizedSearchTerm, searchMatchIds, nodeMap, machineNodeId, bomModalOpen]);

	const currentProducts = filteredProducts.slice(0, currentPage * pageSize);
	const hasMoreProducts = currentPage < totalPages;

	useEffect(() => {
		if (loading || !hasMoreProducts || !loadMoreRef.current) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && !loadMorePendingRef.current) {
					loadMorePendingRef.current = true;
					setCurrentPage((prev) => Math.min(prev + 1, totalPages));
				}
			},
			{ root: null, rootMargin: "400px", threshold: 0 }
		);
		observer.observe(loadMoreRef.current);
		return () => observer.disconnect();
	}, [loading, hasMoreProducts, totalPages]);

	useEffect(() => {
		loadMorePendingRef.current = false;
	}, [currentPage]);

	// Show loading while data is being fetched OR while selectedSite is not available
	const needsBomSelectionReady = hasBomData;
	if (
		loading ||
		bomLoading ||
		(needsBomSelectionReady && !bomSelectionReady) ||
		!selectedSite ||
		selectedSite === ""
	) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "100vh",
				}}
			>
				<CircularProgress />
				<Typography sx={{ ml: 2 }}>
					{!selectedSite || selectedSite === ""
						? t('UI_TEXT.LOADING_SITE_INFO')
						: t('UI_TEXT.LOADING_PARTS')}
				</Typography>
			</Box>
		);
	}

	// Show error state
	if (error) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
				<Alert severity="error" className="error-alert">
					{error}
				</Alert>
			</Box>
		);
	}

	// Only show "no products" after loading is complete AND selectedSite is available
	if (
		!loading &&
		selectedSite &&
		selectedSite !== "" &&
		products.length === 0
	) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
				<Alert severity="info" className="error-alert">
					{t("ALERT_MESSAGES.NO_PRODUCTS_FOUND_MACHINE")}
				</Alert>
			</Box>
		);
	}

	// Route guard - redirect to 404 if machine ID is missing
	if (!machineID) {
		return <PageNotFound />;
	}

	return (
		<>
			<div className="products-page">
				<PageTitle title={pageTitleText} />
				<div className="products-content">
					<div className="filters">
						<div className="filter-content" data-tour="product-search-filter-bom">
							{/* Search and sort controls */}
							<Box sx={{ display: "flex", columnGap: 1, alignItems: "center" }}>
								<Box sx={{ flex: "0 0 80%" }}>
									<ContextFilterAndSort
										value={productSearchTerm}
										onSearchChange={handleProductSearchChange}
										placeholder={t("PLACEHOLDER.SEARCH_PART")}
										options={sortOptions}
										selectedOption={sortOption}
										onSortChange={handleSortChange}
										showSearch={true}
										showSort={true}
									/>
								</Box>
								<Box sx={{ flex: "0 0 20%" }}>
									<TextField
										select
										size="small"
										label={t('GENERAL.PER_PAGE')}
										value={pageSize}
										onChange={handlePageSizeChange}
										fullWidth
										SelectProps={{
											MenuProps: {
												disableScrollLock: true,
											},
										}}
									>
										{pageSizeOptions.map((size) => (
											<MenuItem key={size} value={size}>
												{size}
											</MenuItem>
										))}
									</TextField>
								</Box>
							</Box>
							<Typography variant="body2" sx={{ color: "text.secondary", fontSize: "11px", mt: 0.5 }}>
								{t("UI_TEXT.APPLIED_SORTING", { sort: sortOption || t("MENU_ITEMS.NONE") })}
							</Typography>
							{canShowBulkAddButtons && (
								<Box sx={{ display: "flex", flexDirection: "column", rowGap: 1, width: "100%", alignItems: "stretch", mt: 2 }}>
									{bulkAddCategories.map(({ category, purchasableCount }) => (
										<Button
											key={category}
											variant="outlined"
											startIcon={<ShoppingCartOutlinedIcon />}
											onClick={() => addAllByCategory(category)}
											disabled={purchasableCount === 0}
										>
											{getBulkButtonLabel(category, purchasableCount)}
										</Button>
									))}

								</Box>
							)}


							{/* Product category filters accordion */}
							<Accordion
								defaultExpanded
								sx={{
									marginTop: "10px",
									borderRadius: "5px",
									boxShadow: "none",
									border: "1px solid #ddd",
								}}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									aria-controls="panel1a-content"
									id="panel1a-header"
								>
									<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>
										{t("UI_TEXT.PART_TYPE_FILTERS")}
									</Typography>
								</AccordionSummary>
								<AccordionDetails className="filters-list">
									{/* Select All checkbox */}
									<FormControlLabel
										control={
											<Checkbox
												checked={allCategoriesSelected}
												onChange={handleAllChange}
												value={t('MENU_ITEMS.ALL')}
											/>
										}
										label={`${t('MENU_ITEMS.ALL')} (${products.length})`}
									/>

									{/* Individual category checkboxes with product counts */}
									{availableCategoryArray.map((productType) => {
										const count = products.filter(
											(product) => product.ProductType === productType
										).length;

										let label = productType;

										return (
											products.some(
												(product) => product.ProductType === productType
											) && (
												<FormControlLabel
													key={productType}
													control={
														<Checkbox
															checked={effectiveSelectedFilters.includes(productType)}
															onChange={handleFilterChange}
															value={productType}
														/>
													}
													label={`${label} (${count})`}
												/>
											)
										);
									})}
								</AccordionDetails>
							</Accordion>


							<BomViewer
								{...commonBomViewerProps}
								treeItemRefs={treeItemRefs}
								maxDepth={10}
								onOpenModal={handleOpenBomModal}
								modalOpening={bomModalOpening}
							/>

							{/* Bulk add buttons + Action buttons */}
							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									alignItems: "center",
									justifyContent: "flex-start",
									columnGap: 2,
									marginTop: 2,
									width: "100%",
								}}
							>

								{has3DModel && (
									<Button
										variant="contained"
										startIcon={<ViewInArIcon />}
										onClick={() => handle3DButtonClick()}
									>
										{m3DButtonLoading ? (
											<>
												{t("UI_TEXT.LOADING")}
												<CircularProgress
													color="white"
													size={20}
													sx={{ marginLeft: "5px" }}
												/>
											</>
										) : (
											t("UI_TEXT.MODEL_3D")
										)}
									</Button>
								)}
								{has2DModel && (
									<Button
										variant="contained"
										startIcon={<DescriptionIcon />}
										onClick={() => handle2DButtonClick()}
									>
										{t('UI_TEXT.MODEL_2D')}
									</Button>
								)}
								{shouldShowBomDocIcon && (
									<Button
										variant="contained"
										startIcon={<DescriptionIcon />}
										onClick={handleDocumentationNavigate}
									>
										{t("BUTTON.DOCUMETATION")}
									</Button>
								)}
							</Box>



							{topProductsLoading ? (
								<Box sx={{ mt: 2 }}>
									{/* Skeleton for the title */}
									<Skeleton
										variant="text"
										width="40%"
										height={40}
										sx={{ mb: 2 }}
									/>

									{/* Skeleton list placeholders */}
									<Box>
										{[...Array(4)].map((_, index) => (
											<Box
												key={index}
												sx={{
													display: "flex",
													alignItems: "center",
													mb: 2,
													gap: 2,
												}}
											>
												{/* Image placeholder */}
												<Skeleton variant="rectangular" width={64} height={64} />

												{/* Text placeholders */}
												<Box sx={{ flex: 1 }}>
													<Skeleton width="60%" height={20} />
													<Skeleton width="40%" height={18} />
													<Skeleton width="80%" height={18} />
												</Box>
											</Box>
										))}
									</Box>
								</Box>
							) : (topProducts.length > 0 &&
								topProducts.some((product) => product.purchasable === 1)) ? (
								<Box sx={{ mt: 2 }}>
									<PageTitle subheader={`Best Sellers for ${machine_name}`} />
									<Box>
										{topProducts
											.filter((product) => product.purchasable === 1)
											.slice(0, 4)
											.map((product) => (
												<TopProductListItem
													key={product.product_id}
													product={product}
													machine_id={machineID}
												/>
											))}
									</Box>
								</Box>
							) : null}

						</div>
					</div>

					<Divider flexItem orientation="vertical" sx={{ my: 0.5 }} />

					{/* Main products display area */}
					<div className="products">
						{shouldShowBomSelectionAlert ? (
							<Alert severity="info" sx={{ width: "100%" }}>
								{t("UI_TEXT.SELECT_PARTS_IN_BOM_TREE")}
							</Alert>
						) : (
							<>
								<div className="products-list">
									{currentProducts.map((product) => (
										<div key={product.product_id}>
											<ProductCard
												product={product}
												machineID={machineID}
												showSavedForLaterDial={true}
											/>
										</div>
									))}
								</div>

								{hasMoreProducts && (
									<Box
										ref={loadMoreRef}
										sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 2 }}
									>
										<CircularProgress size={24} />
										<Typography variant="body2" color="text.secondary">
											{t("UI_TEXT.LOADING_MORE_PRODUCTS", { defaultValue: "Loading More Parts" })}
										</Typography>
									</Box>
								)}
							</>
						)}
					</div>
				</div>
			</div>

			{/* Multiple Models Modal */}
			<MultipleModelViewer
				show={modelsModalOpen}
				setShow={setModelsModalOpen}
				machine_number={machine_number}
				machine_name={machine_name}
				productNumbers={[]}
				machine_id={machineID}
				has2D={has2DModel}
				modelData={modelData}
			/>
			<ModalWrapper
				open={bomModalOpen}
				setOpen={setBomModalOpen}
				title={t("UI_TEXT.BOM_TREE_MODAL_TITLE")}
				subtitle={t("UI_TEXT.BOM_TREE_MODAL_SUBTITLE")}
				width="60vw"
				content={
					<BomViewer
						{...commonBomViewerProps}
						variant="modal"
						onNavigateToProduct={handleNavigateToProductDetails}
					/>
				}
			/>
			<BomProductQuickView
				open={bomProductQuickView.open}
				productId={bomProductQuickView.productId}
				machineId={machineID}
				machineName={normalizedMachineName || machine_name || ""}
				onClose={handleCloseBomProductQuickView}
			/>
		</>
	);
};

export default Products;
